/* ============================================================
   Implementation Planner — running the setup by gate.

   The Licensing Navigator answers what must be obtained. This answers
   in what order, what each step unblocks, and what it costs to commit
   ahead of one.

   The figure the rest of the platform does not carry is CAPITAL AT
   RISK: money committed at a gate whose evidence is not yet in hand.
   A vehicle bought before the operator licence is a fixed cost with no
   authority to earn against it, and a warehouse leased before customs
   registration is the same mistake with a longer term on it. Neither
   shows up in a cost model, because a cost model records what was
   spent and not what was true when it was spent.

   Capital is entered by the operator rather than derived. The other
   Build tools size a venture; this one asks what has actually been
   committed, which is a different and usually smaller number.
   ============================================================ */

import { html, raw } from '../../shared/html.js';
import { icon } from '../../shared/icons.js';
import { session } from '../../shared/storage.js';
import { money, percent, parseNumber } from '../../shared/format.js';
import { PrintButton } from '../../components/PrintButton.js';
import { readable } from '../../shared/tone.js';
import { setPageMeasure } from '../../shared/measure.js';
import { AGENCIES, GATES, WORKSTREAMS, INDICATORS, CONTINGENCIES } from '../../data/implementation.js';

const STORAGE_KEY = 'implementation_progress';
const WS_STATUS = ['Not started', 'Under way', 'On track', 'At risk'];

const getState = () => migrate(session.get(STORAGE_KEY, {}) || {});
const save = (next) => session.set(STORAGE_KEY, next);

/* Evidence ticks are keyed by the criterion, not by its position in the
   list. Keying by index meant that inserting or reordering a criterion
   silently moved every tick below it onto a different line, so a saved
   programme would show evidence in hand against something nobody had
   confirmed — the one failure mode a gate register cannot have. Hashing
   the text also drops a tick when the criterion is reworded, which is
   the behaviour you want: what was confirmed is no longer what is asked. */
const fnv = (str) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0).toString(36);
};
const evKey = (g, text) => `${g.id}|${fnv(text)}`;

/* Ticks saved under the old index-based keys, moved across once. Without
   this the fix reads to a user as "the tool forgot my progress". */
const migrate = (s) => {
  let moved = false;
  const next = { ...s };
  GATES.forEach((g) => g.evidence.forEach((text, i) => {
    const old = `${g.id}|${i}`;
    if (Object.prototype.hasOwnProperty.call(next, old)) {
      if (next[old]) next[evKey(g, text)] = true;
      delete next[old];
      moved = true;
    }
  }));
  /* Written back on load, not merely mapped on read: otherwise a user who
     opens the page and ticks nothing keeps the old keys indefinitely, and
     every read pays for the migration again. */
  if (moved) save(next);
  return moved ? next : s;
};

const evidenceDone = (g, s) => g.evidence.filter((text) => s[evKey(g, text)]).length;
const gateReady = (g, s) => evidenceDone(g, s) === g.evidence.length;
const capOf = (g, s) => Number(s[`cap:${g.id}`]) || 0;
/* Committed without the evidence that justifies it. */
const atRisk = (g, s) => (gateReady(g, s) ? 0 : capOf(g, s));

/* A gate whose predecessor has not closed cannot legitimately be
   worked, and work banked there is the dependency error this tool
   exists to surface. */
const blocked = (g, s) => {
  if (!g.needs) return false;
  const prev = GATES.find((x) => x.id === g.needs);
  return prev ? !gateReady(prev, s) : false;
};

export function ImplementationPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';
  page.innerHTML = html`
    <header class="page-head">
      <h1>Implementation Planner</h1>
      <p>
        A Kenyan logistics setup answers to six or seven agencies at once, and several of them
        will not open a file until another has issued something first. The binding constraint is
        never one agency's turnaround — it is the dependency chain. Plan against the gates, record
        what you have actually committed, and the tool will tell you what is exposed.
      </p>
    </header>
    <div id="impl-body"></div>
  `;
  const body = page.querySelector('#impl-body');
  const paint = (focusId) => render(body, paint, focusId);
  paint();
  setPageMeasure(page);
  return page;
}

function render(container, repaint, focusId = null) {
  const s = getState();
  container.replaceChildren();

  const totalEv = GATES.reduce((a, g) => a + g.evidence.length, 0);
  const doneEv = GATES.reduce((a, g) => a + evidenceDone(g, s), 0);
  const pct = Math.round((doneEv / totalEv) * 100);
  const risk = GATES.reduce((a, g) => a + atRisk(g, s), 0);
  const committed = GATES.reduce((a, g) => a + capOf(g, s), 0);
  const current = GATES.find((g) => !gateReady(g, s)) || null;
  const riskGates = GATES.filter((g) => atRisk(g, s) > 0);
  const outOfOrder = GATES.filter((g) => blocked(g, s) && evidenceDone(g, s) > 0);
  const wsRisk = WORKSTREAMS.filter((w) => s[`ws:${w.id}`] === 'At risk');
  const wsIdle = WORKSTREAMS.filter((w) => !s[`ws:${w.id}`] || s[`ws:${w.id}`] === 'Not started');

  /* ---- verdict ----
     Capital exposed outranks everything: a date can still be moved and
     money already committed cannot be un-committed. */
  const colour = risk > 0 ? 'var(--ca-danger)' : current ? 'var(--ca-warning)' : 'var(--ca-positive)';
  const headline = risk > 0
    ? `${money(risk, 'USD')} committed ahead of its evidence`
    : current ? `${current.n}` : 'Every gate closed';
  const detailLine = risk > 0
    ? `${riskGates.map((g) => g.n.split(' — ')[0]).join(', ')} ${riskGates.length > 1 ? 'carry' : 'carries'} capital against incomplete evidence. This is the exposure a cost model cannot show you.`
    : current
      ? `${current.evidence.length - evidenceDone(current, s)} item${current.evidence.length - evidenceDone(current, s) === 1 ? '' : 's'} outstanding before this gate opens${capOf(current, s) ? '' : ', and nothing committed against it yet — the right order'}.`
      : `${money(committed, 'USD')} committed, every gate evidenced. The setup is being run in the right order.`;

  const summary = document.createElement('div');
  summary.innerHTML = html`
    <div class="card avoid-break" style="margin-bottom: var(--ca-space-5); border-color: color-mix(in srgb, ${colour} 30%, var(--ca-border));">
      <div class="row" style="margin-bottom: var(--ca-space-3);">
        <span style="color:${readable(colour)};">${raw(icon(risk > 0 ? 'alert' : current ? 'clock' : 'check', 18))}</span>
        <h2 style="flex:1; font-size: var(--ca-text-base);">${headline}</h2>
        <span class="badge num" style="background: color-mix(in srgb, ${colour} 12%, transparent); color:${readable(colour)};">
          ${percent(pct)}
        </span>
      </div>
      <div class="progress-track" style="margin-bottom: var(--ca-space-3);">
        <div class="progress-fill" style="width:${pct}%; background:${colour};"></div>
      </div>
      <p style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary);">
        <span class="num">${doneEv}</span> of <span class="num">${totalEv}</span> evidence items in hand
        · <span class="num">${money(committed, 'USD')}</span> committed. ${detailLine}
      </p>
      ${outOfOrder.length
        ? html`<p style="margin-top: var(--ca-space-3); font-size: var(--ca-text-sm); color:${readable('var(--ca-warning)')};">
             Work has been recorded at ${outOfOrder.map((g) => g.n.split(' — ')[0]).join(', ')} while the gate before it is still open.
             Applications lodged out of order are usually returned unopenable — worth re-checking the whole chain, not just this one.
           </p>`
        : ''}
    </div>
  `;
  container.appendChild(summary);

  /* ---- gates ---- */
  for (const g of GATES) {
    const done = evidenceDone(g, s);
    const ready = done === g.evidence.length;
    const exposure = atRisk(g, s);
    const isBlocked = blocked(g, s);
    const card = document.createElement('section');
    card.className = 'card avoid-break';
    card.style.marginBottom = 'var(--ca-space-3)';
    if (exposure > 0) card.style.borderColor = 'color-mix(in srgb, var(--ca-danger) 40%, var(--ca-border))';
    else if (ready) card.style.borderColor = 'color-mix(in srgb, var(--ca-positive) 35%, var(--ca-border))';

    card.innerHTML = html`
      <div class="row" style="margin-bottom: var(--ca-space-2);">
        <h3 style="flex:1; font-size: var(--ca-text-sm);">${g.n}</h3>
        <span class="badge num" style="background: var(--ca-surface-sunk); color: var(--ca-text-tertiary);">
          ${done}/${g.evidence.length}
        </span>
      </div>
      <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin-bottom: var(--ca-space-2);">
        ${g.agencies.map((a) => AGENCIES[a]).join(' · ')}
      </p>
      <p style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-3);">${g.why}</p>
      ${isBlocked
        ? html`<p style="font-size: var(--ca-text-xs); color:${readable('var(--ca-warning)')}; margin-bottom: var(--ca-space-3);">
             Depends on ${GATES.find((x) => x.id === g.needs).n}, which is still open.
           </p>`
        : ''}
      <div class="stack" style="gap: var(--ca-space-2);" data-ev></div>
      <div class="row" style="margin-top: var(--ca-space-3); gap: var(--ca-space-2); flex-wrap: wrap;">
        <label for="cap-${g.id}" style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">
          Capital committed at this gate
        </label>
        <input id="cap-${g.id}" type="number" min="0" step="1000" inputmode="numeric"
               value="${capOf(g, s) || ''}" placeholder="0"
               style="width: 140px; text-align: right;">
      </div>
      <p style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin-top: var(--ca-space-2);">
        Releases: ${g.releases}
      </p>
      ${exposure > 0
        ? html`<p style="font-size: var(--ca-text-sm); margin-top: var(--ca-space-2); color:${readable('var(--ca-danger)')};">
             ${money(exposure, 'USD')} committed against evidence that is not yet in hand.
           </p>`
        : ''}
    `;

    const evHost = card.querySelector('[data-ev]');
    g.evidence.forEach((text) => {
      const key = evKey(g, text);
      const label = document.createElement('label');
      label.className = 'check-row';
      label.dataset.item = key;
      label.innerHTML = html`
        <input type="checkbox" ${s[key] ? 'checked' : ''}>
        <span style="font-size: var(--ca-text-sm);">${text}</span>
      `;
      label.querySelector('input').addEventListener('change', (e) => {
        const next = { ...getState() };
        if (e.target.checked) next[key] = true; else delete next[key];
        save(next);
        repaint(key);
      });
      evHost.appendChild(label);
    });

    card.querySelector(`#cap-${g.id}`).addEventListener('input', (e) => {
      const next = { ...getState() };
      const v = Math.max(0, parseNumber(e.target.value) || 0);
      if (v) next[`cap:${g.id}`] = v; else delete next[`cap:${g.id}`];
      save(next);
      repaint(`cap-${g.id}`);
    });

    container.appendChild(card);
  }

  /* ---- workstreams ---- */
  const ws = document.createElement('section');
  ws.className = 'card avoid-break';
  ws.style.marginBottom = 'var(--ca-space-3)';
  ws.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-2);">Workstreams</h3>
    <p style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); margin-bottom: var(--ca-space-3);">
      Six, run concurrently. A single bar labelled "setup" tells an operator nothing they can act on,
      because each of these is gated by a different thing.
    </p>
    <div class="stack" style="gap: var(--ca-space-3);" data-ws></div>
  `;
  const wsHost = ws.querySelector('[data-ws]');
  for (const w of WORKSTREAMS) {
    const row = document.createElement('div');
    row.innerHTML = html`
      <div class="row" style="gap: var(--ca-space-2); flex-wrap: wrap;">
        <div style="flex:1; min-width: 200px;">
          <strong style="font-size: var(--ca-text-sm);">${w.n}</strong>
          ${w.lead ? html`<span class="badge" style="margin-left:.4rem;">critical path</span>` : ''}
          <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary); margin-top: 2px;">${w.dep}</div>
          <div style="font-size: var(--ca-text-xs); color: var(--ca-text-tertiary);">${w.owner} · ${w.cadence}</div>
        </div>
        <select data-ws-id="${w.id}" aria-label="${w.n} status" style="min-width: 150px;">
          ${WS_STATUS.map((o) =>
            (s[`ws:${w.id}`] || 'Not started') === o
              ? html`<option selected>${o}</option>`
              : html`<option>${o}</option>`)}
        </select>
      </div>
    `;
    row.querySelector('select').addEventListener('change', (e) => {
      const next = { ...getState() };
      next[`ws:${w.id}`] = e.target.value;
      save(next);
      repaint();
    });
    wsHost.appendChild(row);
  }
  container.appendChild(ws);

  /* ---- indicators and contingencies ---- */
  const ind = document.createElement('section');
  ind.className = 'card avoid-break';
  ind.style.marginBottom = 'var(--ca-space-3)';
  ind.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-2);">Leading indicators</h3>
    <p style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); margin-bottom: var(--ca-space-3);">
      Each is observable before the thing it predicts, which is the only property that makes an
      indicator worth collecting.
    </p>
    <div class="table-scroll">
      <table>
        <thead><tr><th>Indicator</th><th>Reported</th><th>Predicts</th><th>Escalate when</th></tr></thead>
        <tbody>
          ${INDICATORS.map((i) => html`<tr>
            <td><strong>${i.k}</strong></td><td>${i.freq}</td>
            <td style="color: var(--ca-text-tertiary);">${i.predicts}</td><td>${i.trigger}</td>
          </tr>`)}
        </tbody>
      </table>
    </div>
  `;
  container.appendChild(ind);

  const con = document.createElement('section');
  con.className = 'card avoid-break';
  con.style.marginBottom = 'var(--ca-space-5)';
  con.innerHTML = html`
    <h3 style="font-size: var(--ca-text-sm); margin-bottom: var(--ca-space-2);">Contingencies, agreed in advance</h3>
    <p style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); margin-bottom: var(--ca-space-3);">
      The value of a contingency is entirely in having decided before the pressure arrives.
    </p>
    <div class="stack" style="gap: var(--ca-space-3);">
      ${CONTINGENCIES.map((c) => html`<div>
        <strong style="font-size: var(--ca-text-sm);">If ${c.when}</strong>
        <div style="font-size: var(--ca-text-sm); color: var(--ca-text-tertiary); margin-top: 2px;">${c.then}</div>
      </div>`)}
    </div>
  `;
  container.appendChild(con);

  /* ---- actions ---- */
  const actions = document.createElement('div');
  actions.className = 'no-print';
  actions.appendChild(
    PrintButton({
      targetId: 'impl-body',
      title: 'Implementation Planner',
      subtitle: risk > 0
        ? `${money(risk, 'USD')} committed ahead of its evidence · ${pct}% of gate evidence in hand`
        : `${pct}% of gate evidence in hand · ${money(committed, 'USD')} committed, none ahead of its gate`
    })
  );
  const reset = document.createElement('div');
  reset.style.cssText = 'text-align:center; margin-top: var(--ca-space-3);';
  const btn = document.createElement('button');
  btn.className = 'btn btn-ghost';
  btn.type = 'button';
  btn.textContent = 'Clear the plan';
  btn.addEventListener('click', () => { session.remove(STORAGE_KEY); repaint(); });
  reset.appendChild(btn);
  actions.appendChild(reset);
  container.appendChild(actions);

  if (focusId) {
    const el = container.querySelector(`[data-item="${CSS.escape(focusId)}"] input`) ||
               container.querySelector(`#${CSS.escape(focusId)}`);
    if (el) el.focus({ preventScroll: true });
  }
}
